import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Footer, openModal } from "risa-oasis-ui_v2";
import { v4 as uuidv4 } from "uuid";
import { updateQuestionaireResponse } from "../../api/bigQuery/nycbsPharmaOrders";
import {
  getFileDownloadUrl,
  getFileFromFirebaseStorage,
} from "../../api/firebase/firestoreService";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import DocumentsModal from "../../components/modals/documentsModal/documentsModal";
import PdfRender from "../../components/pdfRender/pdfRender";
import { PharmaQuestionModel } from "../../data-model/pharmaQuestion";
import { NycbsPharmaOrderKeys } from "../../enums/nycbsPharmaOrder";
import { ScreenNames } from "../../enums/screenNames";
import { fetchSingleOrderDetailsForCmm } from "../../redux/slice/cmm/cmmSingleOrderSlice";
import { fetchAllQuestionFromBigQuery } from "../../redux/slice/cmm/pharmaQuestionsSlice";
import { AppDispatch, RootState } from "../../redux/store/store";
import BackArrowBlue from "../../svg/back-arrow-blue";
import { CopyIcon } from "../../svg/copy-icon";
import { logDataToConsole } from "../../utils/customLogger";
import { generateFullName } from "../../utils/generateOrderDataForTable";
import { capitalizeString } from "../../utils/stringModifications";
import PharmaQuestions from "./components/pharmaQuestions";
import {
  generateTimestamp,
  processQuestions,
} from "./utils/pharamaQuestionHelper";

const PharmaQuestionaireLayout = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    data: pharmaQuestionaire,
    loading,
    error,
  } = useSelector((state: RootState) => state.pharmaQuestions);
  const { data: singleCmmOrderData, loading: singleCmmOrderDataLoading } =
    useSelector((state: RootState) => state.cmmSingleOrder);

  const [questionaire, setQuestionaire] = useState<PharmaQuestionModel[]>([]);
  const { id } = useParams();
  const [questionsWithResponse, setQuestionsWithResponse] = useState<
    PharmaQuestionModel[]
  >([]);
  const [documents, setDocuments] = useState<File[]>([]);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setIsOpenModal] = useState<boolean>(false);
  const [errorInFetching, setErrorInFetching] = useState<boolean>(false);
  const [isFetchingDoc, setIsFetchingDoc] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const { downloadURL } = useSelector((state: RootState) => state.fileUpload);
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleCancelClick = () => {
    logDataToConsole("Cancel clicked");
    handleGoBack();
  };

  const uuid = useMemo(() => uuidv4(), []);

  const fetchDownloadUrl = async (refUrl: string) => {
    setIsFetchingDoc(true);
    try {
      const downloadUrl = await getFileDownloadUrl(refUrl);
      const downloadedDocument = await getFileFromFirebaseStorage(downloadUrl);
      setDocuments([downloadedDocument]);
    } catch (err) {
      logDataToConsole("Error fetching document", err);
      setErrorInFetching(true);
      setDocuments([]);
    } finally {
      setIsFetchingDoc(false);
    }
  };

  React.useEffect(() => {
    if (id) {
      dispatch(fetchAllQuestionFromBigQuery(id));
      dispatch(fetchSingleOrderDetailsForCmm(id));
    }
  }, [id]);

  React.useEffect(() => {
    if (pharmaQuestionaire) {
      setQuestionaire(pharmaQuestionaire?.questions as PharmaQuestionModel[]);
      setPdfUrl(pharmaQuestionaire?.file_path ?? "");
      setFileNames([pharmaQuestionaire?.document_name ?? ""]);
      setQuestionsWithResponse(
        pharmaQuestionaire?.questions as PharmaQuestionModel[],
      );
    }
  }, [pharmaQuestionaire, loading, error]);

  React.useEffect(() => {
    if (pdfUrl) {
      fetchDownloadUrl(pdfUrl);
    }
  }, [pdfUrl]);

  React.useEffect(() => {
    modalOpen && openModal("documents-modal");
  }, [modalOpen]);

  React.useEffect(() => {
    if (downloadURL) {
      const newData = {
        ...pharmaQuestionaire,
        file_path: `onco_emr/${pharmaQuestionaire?.identifier}/additional_docs/${uuid}.pdf`,
        questions: processQuestions(questionsWithResponse),
        created_at: generateTimestamp(),
      };
      logDataToConsole("Final Data : ", newData);
      updateQuestionaireResponse(
        newData,
        id ?? "",
        ScreenNames.PHARMA_PA_QUESTIONNAIRE,
        user?.email ?? "",
      );
      setSubmitting(false);
      navigate("/pharma-pa-worklists/");
    }
  }, [downloadURL]);

  return (
    <div className="pharma-questionaire-form__container flex h-full flex-col bg-[#F7F9FA] px-[0.9375rem] py-[0.625rem]">
      {singleCmmOrderDataLoading || loading ? (
        <>
          <LoaderMessage message={"Fetching relevant information..."} />
        </>
      ) : (
        <>
          <div className="pharma-questionaire-form__header mb-4">
            <div
              className="pharma-questionaire-form__header-content flex items-center gap-1 text-h12 font-bold text-tertiaryBlue-4 hover:cursor-pointer"
              onClick={handleGoBack}
            >
              <BackArrowBlue />
              Go Back
            </div>
          </div>
          <div className="pharma-questionaire-form__body flex flex-1 flex-col overflow-hidden rounded bg-white p-4 pb-0">
            <div className="pharma-questionaire-form__body--header mb-4">
              <div className="pharma-questionaire-form__body--header-details flex items-center justify-between gap-2">
                <div className="pharma-questionaire-form__body--header titles flex gap-2">
                  <div className="pharma-questionaire-form__body-page-name flex text-large font-semiBold leading-6 text-black">
                    Attachments and Questionnaire
                  </div>
                  <div className="pharma-questionaire-form__body-patient-name rounded-[1.875rem] bg-primaryGray-16 px-4 text-small font-semibold leading-6 text-primaryGray-1">
                    {capitalizeString(
                      generateFullName(
                        singleCmmOrderData?.[
                          NycbsPharmaOrderKeys.PatientFirstName
                        ] || "",
                        "",
                        singleCmmOrderData?.[
                          NycbsPharmaOrderKeys.PatientLastName
                        ] || "",
                      ),
                    )}
                    &nbsp;&nbsp;<span>&#8226;</span>&nbsp;&nbsp;
                    {singleCmmOrderData?.[NycbsPharmaOrderKeys.PatientMrn] ??
                      ""}
                  </div>
                </div>
                <div className="cmm-result-key ml-auto mr-2 flex items-center gap-2 text-small">
                  Cmm Key:{" "}
                  <span className="font-bold">
                    {singleCmmOrderData?.[NycbsPharmaOrderKeys.CmmResultKey] ??
                      ""}
                    &nbsp;
                  </span>
                  <div
                    className="cmm-result-key copy-icon hover:cursor-pointer"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        singleCmmOrderData?.[
                          NycbsPharmaOrderKeys.CmmResultKey
                        ] ?? "",
                      );
                    }}
                  >
                    <CopyIcon />
                  </div>
                </div>
                <div className="procedure-pa-form__attachements">
                  <Button
                    disabled={false}
                    children={"Manage Attachments"}
                    onClick={() => setIsOpenModal(true)}
                    buttonType={"secondary"}
                    size={"medium"}
                  />
                </div>
              </div>
            </div>
            <div className="pharma-questionaire-form__body-content flex flex-1 overflow-hidden rounded-t-lg border bg-[#F7F9FA]">
              <div className="pharma-questionaire-form__body-content--left ml-4 mr-4 mt-4 w-1/2 flex-1 overflow-auto">
                {isFetchingDoc && (
                  <LoaderMessage message="Fetching documents..." />
                )}
                {errorInFetching &&
                  "Error fetching document. Please try again later."}
                {!isFetchingDoc && documents && documents.length > 0 ? (
                  documents.map((file, index) => (
                    <PdfRender key={index} file={file} />
                  ))
                ) : (
                  <div className="pharma_questionaire-form__docs flex h-full items-center justify-center text-h10">
                    No documents found
                  </div>
                )}
              </div>

              <div className="pharma-questionaire-form__body-content--right flex w-1/2 flex-1 flex-col gap-4 overflow-auto border-l bg-white p-4">
                <>
                  {questionaire && (
                    <PharmaQuestions
                      questions={questionaire}
                      onQuestionUpdated={(questionsWithResponse) =>
                        setQuestionsWithResponse(questionsWithResponse)
                      }
                    />
                  )}
                </>
              </div>
            </div>
          </div>
          <div className="pharma-questionaire-form__footer bg-red-50">
            <Footer
              cancelButtonText="Go Back"
              submitButtonText={submitting ? "Submitting..." : "Submit"}
              onCancelClick={handleCancelClick}
              onSubmitClick={() => {}}
              isDisabled={submitting || documents.length === 0}
            />
          </div>
          {modalOpen && (
            <DocumentsModal
              onClose={() => setIsOpenModal(false)}
              documentsName={fileNames}
              documentsList={documents}
              id={pharmaQuestionaire?.identifier ?? ""}
              newDocumentNames={(urls) => setFileNames(urls)}
              newFiles={(files) => setDocuments(files)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default PharmaQuestionaireLayout;
